import * as Logger from "js-logger";
import { Signal } from "signals";
import { SceneConfig } from "slotworks/config/asset-config-schema";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import AnimationManager from "slotworks/graphics/animation/animation-manager";
import { ScriptedAnimation } from "slotworks/graphics/animation/scripted-animation";
import { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import ButtonElement from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import FlexiInputText from "slotworks/graphics/elements/flexi-input-text";
import FlexiSlider from "slotworks/graphics/elements/flexi-slider";
import FlexiSprite from "slotworks/graphics/elements/flexi-sprite";
import FlexiText from "slotworks/graphics/elements/flexi-text";
import FlexiToggle from "slotworks/graphics/elements/flexi-toggle";
import { SmartShape } from "slotworks/graphics/elements/smart-shape";
import ToggleElement from "slotworks/graphics/elements/toggle-element";
import DOMLayer from "slotworks/graphics/layers/dom-layer";
import InvalidSceneError from "slotworks/graphics/layers/invalid-scene-error";
import { populateSprites } from "slotworks/graphics/layers/populators/populate-sprites";
import TextPopulator from "slotworks/graphics/layers/populators/populate-text";
import Scene from "slotworks/graphics/layers/scene";
import { SceneTransition } from "slotworks/graphics/layers/scene-transitions/scene-transition";
import ParticleManager from "slotworks/graphics/particles/particle-manager";
import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import { clearMap } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import { parseJSONMap } from "slotworks/utils/json-utils";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";
import FlexiVideo from "../elements/flexi-video";

export default class Layers {

    public static All: Layers[] = [];

    public static Background: Layers = new Layers("Background");
    public static MatrixBackground: Layers = new Layers("MatrixBackground");
    public static MatrixContent: Layers = new Layers("MatrixContent");
    public static MatrixForeground: Layers = new Layers("MatrixForeground");
    public static Paylines: Layers = new Layers("Paylines");
    public static SymbolAnimations: Layers = new Layers("SymbolAnimations");
    public static Gamble: Layers = new Layers("Gamble");
    public static Banners: Layers = new Layers("Banners");
    public static Footer: Layers = new Layers("Footer");
    public static Bonus: Layers = new Layers("Bonus");
    public static BonusForeground: Layers = new Layers("BonusForeground");
    public static BetBar: Layers = new Layers("BetBar");
    public static GambleControls: Layers = new Layers("GambleControls");
    public static SmartButton: Layers = new Layers("SmartButton");
    public static CelebrationBackground: Layers = new Layers("CelebrationBackground");
    public static CelebrationContent: Layers = new Layers("CelebrationContent");
    public static CelebrationForeground: Layers = new Layers("CelebrationForeground");
    public static Menus: Layers = new Layers("Menus");
    public static MenusContent: Layers = new Layers("MenusContent");
    public static PaytableContent: Layers = new Layers("PaytableContent");
    public static BetBarPopups: Layers = new Layers("BetBarPopups");
    public static Prompts: Layers = new Layers("Prompts");
    public static Alerts: Layers = new Layers("Alerts");
    public static Header: Layers = new Layers("Header");
    public static Debug: Layers = new Layers("Debug");
    public static Custom: Map<string, Layers> = new Map<string, Layers>();

    public static onEnterScene: Signal = new Signal();
    public static onExitScene: Signal = new Signal();

    public static FromString(id: string): Layers {
        return Layers.IdMap.get(id);
    }

    /**
     * Creates a pixi container for every layer, and stores the initial config from assetworks in each layer.
     * Then automatically calls resetLayer on each layer to populate their initial graphics
     *
     * @param config { Map<string, SceneConfig> }
     */
    public static init(config: SceneConfig[]) {

        const configMap = parseJSONMap<SceneConfig>(config);

        const stage = CanvasManager.stage;

        // Get a list of all layers, core and custom
        const layerNames: string[] = [];

        for (const layer of Layers.All) {
            layerNames.push(layer.id);
        }

        let insertIndex = 0;

        configMap.forEach((sceneConfig: SceneConfig, layerSceneName: string) => {
            const layerName = layerSceneName.split("-")[0];

            if (layerName === "DOM") {
                return;
            }

            if (layerNames.indexOf(layerName) < 0) {
                const layer = new Layers(layerName, insertIndex);
                layerNames.splice(insertIndex, 0, layerName);
                Layers.Custom.set(layerName, layer);
            }

            insertIndex = layerNames.indexOf(layerName);
        });

        // Create all layers
        for (const layer of Layers.All) {
            const layerContainer = new PIXI.Container();

            layerContainer.name = layer.id;
            layer.container = layerContainer;

            stage.addChild(layerContainer);

            // Set up layers with information about their scenes

            const sceneConfigs: Map<string, SceneConfig> = new Map<string, SceneConfig>();

            configMap.forEach((sceneConfig: SceneConfig, layerSceneName: string) => {
                const sceneName = layerSceneName.split("-").length > 1 ? layerSceneName.split("-")[1] : "default";

                if (layerSceneName.split("-")[0] === layer.id) {
                    sceneConfigs.set(sceneName.toLowerCase(), sceneConfig);
                }
            });

            layer.init(sceneConfigs);
        }
    }

    /**
     * Calls set scene on all layers
     *
     * @param scene {string}
     */
    public static jumpToScene(scene: string) {
        for (const layer of Layers.All) {
            layer.jumpToScene(scene);
        }
    }

    /**
     * Purely syntactic sugar. Calls set scene with no args
     */
    public static jumpToDefaultScene() {
        this.jumpToScene("default");
    }

    /**
     * Clears all layers of all contents, auto generated or otherwise
     */
    public static clearAllLayers() {
        for (const layer of Layers.All) {
            layer.clear();
        }
    }

    public static hideAll() {
        for (const layer of Layers.All) {
            layer.hide();
        }
    }

    public static showAll() {
        for (const layer of Layers.All) {
            layer.show();
        }
    }

    public static listLayers() {
        for (const child of Layers.Debug.container.parent.children) {
            Logger.info(child.name);
        }
    }

    private static IdMap: Map<string, Layers>;

    public id: string;
    public container: PIXI.Container;
    public bounds: Rectangle;
    public portraitBounds: Rectangle;

    // Signal dispatched before elements are automatically moved on an orientation change
    public preOrientationChange: Signal = new Signal();
    public onSceneExit: Signal = new Signal();
    public onSceneEnter: Signal = new Signal();

    private boundsMask: PIXI.Graphics;
    private customMask: Rectangle;
    private customMaskGraphic: PIXI.Graphics | PIXI.Sprite;

    private cachingAsBitmap: boolean;
    private cacheAsBitmapTimer: number;

    private currentScene: Scene;
    private scenes: Map<string, Scene> = new Map<string, Scene>();
    private positions: Map<string, Rectangle>;
    private particles: Map<string, number> = new Map<string, number>();
    private scriptedAnimations: Map<string, ScriptedAnimation> = new Map<string, ScriptedAnimation>();

    private buttonElements: Map<string, ButtonElement> = new Map<string, ButtonElement>();
    private toggleElements: Map<string, ToggleElement> = new Map<string, ToggleElement>();
    private flexiButtons: Map<string, FlexiButton> = new Map<string, FlexiButton>();
    private flexiTexts: Map<string, FlexiText> = new Map<string, FlexiText>();
    private flexiSprites: Map<string, FlexiSprite> = new Map<string, FlexiSprite>();
    private flexiToggles: Map<string, FlexiToggle> = new Map<string, FlexiToggle>();
    private flexiInputTexts: Map<string, FlexiInputText> = new Map<string, FlexiInputText>();
    private flexiSliders: Map<string, FlexiSlider> = new Map<string, FlexiSlider>();
    private flexiVideos: Map<string, FlexiVideo> = new Map<string, FlexiVideo>();

    constructor(id: string, depth: number = -1) {
        this.id = id;

        if (depth === -1) {
            Layers.All.push(this);
        } else {
            Layers.All.splice(depth, 0, this);
        }

        if (!Layers.IdMap) {
            Layers.IdMap = new Map<string, Layers>();
        }
        Layers.IdMap.set(id, this);
    }

    public init(scenes: Map<string, SceneConfig>) {
        scenes.forEach((config, name, original) => {
            name = name.toLowerCase();
            this.scenes.set(name, new Scene(name, this.id, config));
        });

        CanvasManager.onOrientationChange.add((orientation: Orientation) => this.updateOrientation(orientation));
    }

    public addScriptedAnimation(name: string, anim: ScriptedAnimation) {
        this.scriptedAnimations.set(name, anim);
    }

    public getScriptedAnimation(name: string) {
        return this.scriptedAnimations.get(name);
    }

    public addParticle(name: string, id: number) {
        this.particles.set(name, id);
    }

    public getParticle(id: string) {
        return ParticleManager.get(this.particles.get(id));
    }

    public addButton(id: string, button: ButtonElement) {
        this.buttonElements.set(id, button);
    }

    public getButton(id: string): ButtonElement {
        return this.buttonElements.get(id);
    }

    public getSprite(id: string): PIXI.Sprite {
        return this.container.getChildByName(id) as PIXI.Sprite;
    }

    public getSprites(ids: string[]): PIXI.Sprite[] {
        const sprites = [];
        for (const id of ids) {
            sprites.push(this.getSprite(id));
        }
        return sprites;
    }

    public getSpine(id: string) {
        return this.container.getChildByName(id) as PIXI.spine.Spine;
    }

    public getAnimatedSprite(id: string): AnimatedSprite {
        return this.container.getChildByName(id) as AnimatedSprite;
    }

    public hasAssetOfName(name: string): boolean {
        if (this.getDOMElements(name).length > 0) {
            return true;
        }

        for (const child of this.container.children) {
            if (child.name === name) {
                return true;
            }
        }

        return false;
    }

    public getFlexiSprite(id: string): FlexiSprite {

        if (this.flexiSprites.get(id)) {
            return this.flexiSprites.get(id);
        }

        const domElements = this.getDOMElements(id);
        const sprite = this.getSprite(id);

        const flexiSprite = new FlexiSprite();

        flexiSprite.addTargets(domElements);
        if (sprite) {
            flexiSprite.addTarget(sprite);
        }

        if (flexiSprite.hasTargets()) {
            this.flexiSprites.set(id, flexiSprite);
            return flexiSprite;
        }
        return null;
    }

    public addToggle(id: string, toggle: ToggleElement) {
        this.toggleElements.set(id, toggle);
    }

    public getToggle(id: string): ToggleElement {
        return this.toggleElements.get(id);
    }

    public getFlexiButton(id: string): FlexiButton {

        if (this.flexiButtons.get(id)) {
            return this.flexiButtons.get(id);
        }

        const domElements = this.getDOMElements(id);
        const sprite = this.getSprite(id);
        const button = this.getButton(id);

        const flexiButton = new FlexiButton();

        flexiButton.addTargets(domElements);
        flexiButton.addTarget(button);
        flexiButton.addTarget(sprite);

        if (flexiButton.hasTargets()) {
            flexiButton.setEnabled(true);
            flexiButton.setVisible(true);

            this.flexiButtons.set(id, flexiButton);
            return flexiButton;
        }
        return null;
    }

    public getText(id: string): MultiStyleText {
        return this.container.getChildByName(id) as MultiStyleText;
    }

    public getBitmapText(id: string): PIXI.extras.BitmapText {
        return this.container.getChildByName(id) as PIXI.extras.BitmapText;
    }

    public getFlexiText(id: string): FlexiText {

        if (this.flexiTexts.get(id)) {
            return this.flexiTexts.get(id);
        }

        const domElements = this.getDOMElements(id);
        const text = this.getText(id);

        const flexiText = new FlexiText();

        flexiText.addTargets(domElements);
        if (text) {
            flexiText.addTarget(text);
        }

        if (flexiText.hasTargets()) {
            this.flexiTexts.set(id, flexiText);
            return flexiText;
        }
        return null;
    }

    public getFlexiInputText(id: string): FlexiInputText {

        if (this.flexiInputTexts.get(id)) {
            return this.flexiInputTexts.get(id);
        }

        const domElements = this.getDOMElements(id);

        const flexiInputText = new FlexiInputText();

        flexiInputText.addTargets(domElements);

        if (flexiInputText.hasTargets()) {
            this.flexiInputTexts.set(id, flexiInputText);
            return flexiInputText;
        }
        return null;
    }

    public getFlexiToggle(id: string) {

        if (this.flexiToggles.get(id)) {
            return this.flexiToggles.get(id);
        }

        const domElements = this.getDOMElements(id);
        const toggle = this.getToggle(id);

        const flexiToggle = new FlexiToggle();

        flexiToggle.addTargets(domElements);
        flexiToggle.addTarget(toggle);

        if (flexiToggle.hasTargets()) {
            this.flexiToggles.set(id, flexiToggle);
            return flexiToggle;
        }
        return null;
    }

    // TODO: Make all flexi builders based on generic function
    public getFlexiSlider(id: string) {

        if (this.flexiSliders.get(id)) {
            return this.flexiSliders.get(id);
        }

        const domElements = this.getDOMElements(id);

        const flexiSlider = new FlexiSlider();

        flexiSlider.addTargets(domElements);

        if (flexiSlider.hasTargets()) {
            this.flexiSliders.set(id, flexiSlider);
            return flexiSlider;
        }
        return null;
    }

    public getFlexiVideo(id: string): FlexiVideo {
        if (this.flexiVideos.get(id)) {
            return this.flexiVideos.get(id);
        }

        const domElements = this.getDOMElements(id) as HTMLVideoElement[];
        const flexiVideo = new FlexiVideo();
        flexiVideo.addTargets(domElements);

        if (flexiVideo.hasTargets()) {
            this.flexiVideos.set(id, flexiVideo);
            return flexiVideo;
        }

        return null;
    }

    public getDOMElements(id: string): HTMLElement[] {
        return Array.from(document.querySelectorAll("#" + this.id + " #" + this.currentScene.name + " #" + id)) as HTMLElement[];
    }

    public getShape(id: string): PIXI.Graphics {
        return this.container.getChildByName(id) as PIXI.Graphics;
    }

    public getPosition(id: string, layer?: Layers): Rectangle {
        let position = this.positions.get(id);
        if (!position) {
            return position;
        }

        position = position.clone();

        if (layer) {
            position = this.localToLocal(layer, position);
        }

        return position;
    }

    public add(target: PIXI.DisplayObject, globalToLocal: boolean = false, sendToBack: boolean = false) {
        if (target.parent) {
            const currentLayer = Layers.FromString(target.parent.name).container;

            target.x += currentLayer.x - this.container.x;
            target.y += currentLayer.y - this.container.y;
        }

        if (globalToLocal) {
            target.x -= this.container.x;
            target.y -= this.container.y;
        }

        if (sendToBack) {
            this.container.addChildAt(target, 0);
        } else {
            this.container.addChild(target);
        }
    }

    public remove(target: PIXI.Container) {
        this.container.removeChild(target);
    }

    public setTransitions(scene: string, transition: SceneTransition, targetScene: string = "*") {
        this.getScene(scene).setTransitions(transition, targetScene);
    }

    public hasScene(name: string) {
        name = name.toLowerCase();
        return this.scenes.has(name) || DOMLayer.hasDOMScene(name, this.id);
    }

    public hasAnyScene(names: string[]) {

        for (const name of names) {
            if (this.hasScene(name)) {
                return true;
            }
        }

        return false;
    }

    public hasScenes(names: string[]) {
        for (const name of names) {
            if (!this.hasScene(name)) {
                return false;
            }
        }

        return true;
    }

    public getScene(scene: string | Scene): Scene {
        if (!(scene instanceof Scene)) {
            return this.getSceneByName(scene);
        }
        return scene;
    }

    public getFirstValidScene(scenes: string[]): Scene {
        for (const scene of scenes) {
            if (this.hasScene(scene)) {
                return this.getScene(scene);
            }
        }

        return null;
    }

    public jumpToScene(scene: string | Scene, autoClear: boolean = true) {
        const nextScene = this.getScene(scene);

        if (this.currentScene) {
            if (autoClear) {
                this.clear();
            }

            this.currentScene.clearDOM();
            this.currentScene.destroyTransitions();
            Layers.onExitScene.dispatch(this.id, this.currentScene.name);
        }

        this.currentScene = nextScene;

        this.currentScene.showDOM();

        Layers.onEnterScene.dispatch(this.id, this.currentScene.name);

        if (this.currentScene.hasCanvas()) {
            this.updateBounds();
            populateSprites(this.currentScene.config.sprites, this);
            TextPopulator.populateText(this.currentScene.config.text, this);
            this.updatePositions(this.currentScene);
        }

        this.resetContainer();
        this.updateOrientation(CanvasManager.orientation);

        this.onSceneEnter.dispatch(nextScene.name);
    }

    public setScene(scene: string | Scene, setup?: () => void, autoClear: boolean = true): Contract<void> {
        const nextScene = this.getScene(scene);

        const currentSceneName = this.currentScene ? this.currentScene.name : "*";

        return new Contract<void>((resolve) => {
            const enterNextScene = () => {
                this.jumpToScene(nextScene);

                if (setup) {
                    setup();
                }

                this.currentScene.in(currentSceneName).then(resolve);
            };

            if (this.currentScene) {
                this.currentScene.out(nextScene.name).then(() => {
                    this.onSceneExit.dispatch(currentSceneName);
                    enterNextScene();
                });
            } else {
                enterNextScene();
            }
        });
    }

    public skipTransition() {
        this.currentScene.skipTransition();
    }

    public defaultScene(): Contract<void> {
        return this.setScene("default");
    }

    public getCurrentScene(): Scene {
        return this.currentScene;
    }

    public currentSceneIs(name: string): boolean {
        return this.currentScene && this.currentScene.name === name;
    }

    public hide() {
        this.container.visible = false;
        DOMLayer.setVisibleDOMLayer(this.id, false);
    }

    public show() {
        this.container.visible = true;
        DOMLayer.setVisibleDOMLayer(this.id, true);
    }

    /**
     * Immediately turns off caching, or turns it on with a delay
     */
    public cacheAsBitmap(cache: boolean) {
        this.cachingAsBitmap = cache;

        this.updateCacheAsBitmap(cache);
    }

    public refreshBitmapCache() {
        if (this.cachingAsBitmap) {
            this.updateCacheAsBitmap(false);
            this.updateCacheAsBitmap(true);
        }
    }

    public clear() {
        // Clear bitmap cache if applicable
        this.refreshBitmapCache();

        // If applicable, remove bounds mask from container
        if (this.boundsMask && this.boundsMask.parent === this.container) {
            this.container.removeChild(this.boundsMask);
        }

        // Clean up any auto generated elements
        clearMap(this.buttonElements);
        clearMap(this.toggleElements);

        // Clean up any generated flexis
        clearMap(this.flexiButtons);
        clearMap(this.flexiTexts);
        clearMap(this.flexiSprites);
        clearMap(this.flexiInputTexts);
        clearMap(this.flexiToggles);
        clearMap(this.flexiSliders);

        // Kill all particles
        this.particles.forEach((particleId: number) => {
            ParticleManager.remove(particleId);
        });
        this.particles.clear();

        // Remove all scripted animations
        this.scriptedAnimations.forEach((scriptedAnimation: ScriptedAnimation) => {
            AnimationManager.removeScriptedAnimation(scriptedAnimation);
        });
        this.scriptedAnimations.clear();

        // Clear bounds
        this.portraitBounds = this.bounds = null;

        // Clear custom masks
        if (this.customMask) {
            this.container.mask = null;
            this.customMask = null;
        }
        if (this.customMaskGraphic) {
            this.container.mask = null;
            this.customMaskGraphic = null;
        }

        // Remove all children and kill any tweens that may exist
        while (this.container.children.length) {
            const child = this.container.children[0];
            if (child) {
                if (child.scale) {
                    TweenJS.removeAll(child.scale);
                }
                TweenJS.removeAll(child);
                if (child.destroy) {
                    child.destroy();
                }
            }
        }
    }

    public localToGlobal(point: Rectangle): Rectangle;
    public localToGlobal(point: Point): Point;
    public localToGlobal(input: Point | Rectangle): Point | Rectangle {
        const globalPoint = new Point(input.x + this.container.x, input.y + this.container.y);

        if (input instanceof Rectangle) {
            return new Rectangle(globalPoint.x, globalPoint.y, input.width, input.height);
        } else {
            return globalPoint;
        }
    }

    public globalToLocal(point: Rectangle): Rectangle;
    public globalToLocal(point: Point): Point;
    public globalToLocal(input: Point | Rectangle): Point | Rectangle {
        const localPoint = new Point(input.x - this.container.x, input.y - this.container.y);

        if (input instanceof Rectangle) {
            return new Rectangle(localPoint.x, localPoint.y, input.width, input.height);
        } else {
            return localPoint;
        }
    }

    public localToLocal(layer: Layers, point: Rectangle): Rectangle;
    public localToLocal(layer: Layers, point: Point): Point;
    public localToLocal(layer: Layers, point: Point | Rectangle): Point | Rectangle {
        let global: Point | Rectangle;
        if (point instanceof Point) {
            global = this.localToGlobal(point);
            return layer.globalToLocal(global);
        } else {
            global = this.localToGlobal(point);
            return layer.globalToLocal(global);
        }
    }

    public moveBehind(targetLayer: Layers) {
        const targetDepth = Math.max(0, this.container.parent.getChildIndex(targetLayer.container) - 1);
        this.container.parent.setChildIndex(this.container, targetDepth);
    }

    public moveAbove(targetLayer: Layers) {
        const targetDepth = this.container.parent.getChildIndex(targetLayer.container) + 1;
        this.container.parent.setChildIndex(this.container, targetDepth);
    }

    public enableBoundsMask(enable: boolean) {
        if (this.customMaskGraphic) {
            this.container.mask = this.customMaskGraphic;
        } else {
            if (enable) {
                this.boundsMask.visible = true;
                this.container.mask = this.boundsMask;
            } else if (!this.customMask) {
                this.boundsMask.visible = false;
                this.container.mask = null;
            }
        }
    }

    public debug(color: number = 0xff0000) {
        let boundsStr = "";

        if (this.bounds.width === 0 || this.bounds.height === 0) {
            boundsStr = "(No bounds set)";
        } else {
            boundsStr = "(" + this.bounds.x + ", " + this.bounds.y + " - " + this.bounds.width + "x" + this.bounds.height + ")";
        }

        const graphics = new PIXI.Graphics();

        graphics.beginFill(color, 0.25);
        graphics.lineStyle(2, color, 0.5);
        graphics.drawRect(0, 0, this.bounds.width, this.bounds.height);

        const text = new PIXI.Text(boundsStr, new PIXI.TextStyle({
            fontSize: 12,
            fontWeight: "bold",
            fill: [color]
        }));
        text.y = -18;

        this.container.addChild(text);
        this.container.addChild(graphics);
    }

    public getScenes() {
        return this.scenes;
    }

    public getDOMContainers() {
        if (this.currentScene) {
            return Array.from(document.querySelectorAll("#" + this.id + " #" + this.currentScene.name)) as HTMLElement[];
        }

        return [];
    }

    public setCustomMask(mask: Rectangle): void;
    public setCustomMask(mask: PIXI.Sprite | PIXI.Graphics): void;

    public setCustomMask(mask: Rectangle | PIXI.Sprite | PIXI.Graphics): void {
        if (mask instanceof Rectangle) {
            this.customMask = this.localToGlobal(mask);
            this.container.mask = this.boundsMask;
        } else {
            this.customMaskGraphic = mask;
        }

        this.updateMask();
    }

    private updateCacheAsBitmap(cache: boolean) {
        Timer.clearTimeout(this.cacheAsBitmapTimer);
        if (cache) {
            this.cacheAsBitmapTimer = Timer.setTimeout(() => {
                this.container.cacheAsBitmap = true;
            }, 100);
        } else {
            this.container.cacheAsBitmap = false;
        }
    }

    private updateMask() {
        let bounds = this.bounds;

        if (this.customMaskGraphic) {
            this.customMaskGraphic.renderable = false;
            this.container.addChild(this.customMaskGraphic);
            this.container.mask = this.customMaskGraphic;
        } else {
            if (this.customMask) {
                bounds = this.customMask;
            }

            if (!this.boundsMask) {
                this.boundsMask = new PIXI.Graphics();
            }

            this.boundsMask.clear();
            this.boundsMask.beginFill(0xff0000);
            this.boundsMask.drawRect(bounds.x - this.container.x, bounds.y - this.container.y, bounds.width, bounds.height);
            this.boundsMask.renderable = false;
            this.container.addChild(this.boundsMask);
        }
    }

    private updateOrientation(orientation: Orientation) {
        if (this.currentScene && this.currentScene.config) {

            this.preOrientationChange.dispatch();

            this.refreshBitmapCache();

            let spritePositions;
            let textConfigs;

            if (orientation === Orientation.LANDSCAPE) {
                spritePositions = this.currentScene.landscapeSpritePositions;
                textConfigs = this.currentScene.landscapeTextConfigs;

                // Don't reposition landscape layers if there is no portrait counterpart
                if (!this.portraitBounds && this.currentScene.portraitSpritePositions.size === 0 && this.currentScene.portraitTextConfigs.size === 0) {
                    return;
                }
            } else {
                spritePositions = this.currentScene.portraitSpritePositions;
                textConfigs = this.currentScene.portraitTextConfigs;
            }

            // Reposition / resize sprites
            spritePositions.forEach((position, spriteId) => {
                const sprite = this.getSprite(spriteId);
                const particle = this.getParticle(spriteId);

                if (sprite) {
                    sprite.x = position.x;
                    sprite.y = position.y;

                    if (sprite instanceof PIXI.spine.Spine) {
                        sprite.scale.x = position.width / sprite.spineData.width;
                        sprite.scale.y = position.height / sprite.spineData.height;
                    } else if (sprite instanceof ButtonElement) {
                        sprite.resize(position.width, position.height);
                    } else if (sprite instanceof SmartShape) {
                        sprite.shapeWidth = position.width;
                        sprite.shapeHeight = position.height;
                        sprite.draw();
                    } else {
                        sprite.width = position.width;
                        sprite.height = position.height;
                    }

                    const anchor = (sprite as any).anchor;
                    if (anchor) {
                        if (sprite.scale.x >= 0) {
                            sprite.x += sprite.width * anchor.x;
                        } else {
                            sprite.x += sprite.width * (1 - anchor.x);
                        }
                        if (sprite.scale.y >= 0) {
                            sprite.y += sprite.height * anchor.y;
                        } else {
                            sprite.y += sprite.height * (1 - anchor.y);
                        }
                    }
                } else if (particle) {
                    // Reposition particles
                    particle.parent.x = position.x;
                    particle.parent.y = position.y;
                } else {
                    Logger.warn("When trying to move", spriteId, "the sprite was not found. Layer / scene: ", this.id, this.currentScene.name);
                }
            });

            // Reposition / resize text
            textConfigs.forEach((textConfig, textId) => {
                const text = this.getText(textId);
                if (text) {
                    if (text.resize) {
                        text.resize(textConfig.width, textConfig.height, textConfig.size);
                    }

                    text.move(textConfig.x, textConfig.y, textConfig.align);
                }
            });

            // Reposition / resize entire layer (portrait bounds)
            if (this.portraitBounds && orientation === Orientation.PORTRAIT) {
                this.container.scale.set(this.portraitBounds.width / this.bounds.width, this.portraitBounds.height / this.bounds.height);
                this.container.x = this.portraitBounds.x;
                this.container.y = this.portraitBounds.y;
            } else {
                this.container.scale.set(1, 1);
                this.container.x = this.bounds.x;
                this.container.y = this.bounds.y;
            }
        }
    }

    private getSceneByName(name: string = "default"): Scene {
        name = name.toLowerCase();

        let scene = this.scenes.get(name);

        if (!scene && (DOMLayer.hasDOMScene(name, this.id) || name === "default")) {
            scene = new Scene(name, this.id, null);
        }

        if (!scene) {
            throw new InvalidSceneError("Invalid scene: '" + name + "' for " + this.id);
        }

        return scene;
    }

    private updateBounds() {
        const bounds = this.currentScene.config.bounds;
        const portraitBounds = (this.currentScene.config as any).portraitBounds;

        // Set layer position / bounds
        this.container.x = bounds.x;
        this.container.y = bounds.y;

        this.bounds = new Rectangle(bounds.x, bounds.y, bounds.width, bounds.height);
        if (portraitBounds) {
            this.portraitBounds = new Rectangle(portraitBounds.x, portraitBounds.y, portraitBounds.width, portraitBounds.height);
        }
        this.updateMask();
    }

    private updatePositions(scene: Scene) {
        if (scene.positions) {
            this.positions = scene.positions;
        }
    }

    private resetContainer() {
        this.container.pivot = new PIXI.Point(0, 0);
        this.container.scale.x = 1;
        this.container.scale.y = 1;
        this.container.alpha = 1;
    }
}
